import { c as _c } from "react/compiler-runtime";
// «Мои артикулы»: все коды партнёра с метриками; включить/отключить,
// переименовать (название, площадка, кампания). Заблокированные площадкой
// не переключаются - статус и причина видны в строке.
import { UGC_TEXTS } from "@osmix/contracts/ugc-articles";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";
import { Skeleton } from "@osmix/ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { api, type UgcArticle, type UgcArticleStatus } from "@/api";
import { ErrorState } from "@/components/common/states";
import { XIcon } from "@/components/icons";
import { CodeChip, EmptyNote, fmtInt, fmtPercent, partnerKey, StatusBadge } from "@/components/partner/partner-ui";
import { Button } from "@/components/ui/button";
import { Segmented } from "@/components/ui/segmented";
import { Switch } from "@/components/ui/switch";
import { TextField } from "@/components/ui/text-field";
import { useFormatPrice } from "@/lib/use-format-price";
import { cn } from "@/lib/utils";
import { usePartner } from "./shell";
type Filter = "all" | "active" | "disabled";
export function PartnerArticlesPage() {
  const $ = _c(41);
  const {
    t
  } = useTranslation();
  const {
    partner,
    userId
  } = usePartner();
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const arbitrage = partner.role === "arbitrage";
  const status = filter === "all" ? undefined : filter;
  const t0 = status ?? "all";
  let t1;
  if ($[0] !== t0 || $[1] !== userId) {
    t1 = partnerKey(userId, "articles", t0);
    $[0] = t0;
    $[1] = userId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== status) {
    t2 = () => api.ugc.partner.articles(status ? {
      status
    } : {});
    $[3] = status;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const articles = useQuery(t3);
  let t4;
  if ($[8] !== articles.data) {
    t4 = articles.data ?? [];
    $[8] = articles.data;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const list = t4;
  let t5;
  if ($[10] !== t) {
    t5 = t("partner.articles.all");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = {
      value: "all",
      label: t5
    };
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("partner.articles.active");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = {
      value: "active",
      label: t7
    };
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = t("partner.articles.disabled");
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = {
      value: "disabled",
      label: t9
    };
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t6 || $[24] !== t8) {
    t11 = [t6, t8, t10];
    $[22] = t10;
    $[23] = t6;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== filter || $[27] !== t11) {
    t12 = <Segmented value={filter} onValueChange={setFilter} className="max-w-full overflow-x-auto [scrollbar-width:none]" options={t11} />;
    $[26] = filter;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== arbitrage || $[30] !== articles || $[31] !== list || $[32] !== t) {
    t13 = articles.isPending ? <div className="flex flex-col gap-2">{Array.from({
        length: 4
      }, _temp)}</div> : articles.error ? <ErrorState onRetry={() => void articles.refetch()} /> : list.length === 0 ? <EmptyNote action={<Button asChild={true} size="md"><Link to="/partner/catalog">{t("partner.articles.toCatalog")}</Link></Button>}>{t("partner.texts.empty", {
        defaultValue: UGC_TEXTS.empty
      })}</EmptyNote> : <ul className="flex flex-col gap-2">{list.map(a => <ArticleRow key={a.id} article={a} arbitrage={arbitrage} onEdit={() => setEditing(a)} />)}</ul>;
    $[29] = arbitrage;
    $[30] = articles;
    $[31] = list;
    $[32] = t;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = open => !open && setEditing(null);
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== editing) {
    t15 = <RenameDialog article={editing} onOpenChange={t14} />;
    $[35] = editing;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t12 || $[38] !== t13 || $[39] !== t15) {
    t16 = <section className="flex min-w-0 flex-col gap-4" data-testid="partner-articles">{t12}{t13}{t15}</section>;
    $[37] = t12;
    $[38] = t13;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
}
function _temp(_, i) {
  return <Skeleton key={i} className="h-[112px] rounded-[20px]" />;
}
function useArticleUpdate() {
  const $ = _c(8);
  const {
    t
  } = useTranslation();
  const {
    userId
  } = usePartner();
  const qc = useQueryClient();
  let t0;
  if ($[0] !== qc || $[1] !== userId) {
    t0 = () => {
      qc.invalidateQueries({
        queryKey: partnerKey(userId, "articles")
      });
      qc.invalidateQueries({
        queryKey: partnerKey(userId, "stats")
      });
    };
    $[0] = qc;
    $[1] = userId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t) {
    t1 = e => toast.error(e instanceof Error ? e.message : t("common.error"));
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: _temp2,
      onSuccess: t0,
      onError: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}
function _temp2(t0) {
  const {
    id,
    patch
  } = t0;
  return api.ugc.partner.updateArticle(id, patch);
}
function Metric(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="truncate text-[11px] leading-[14px] font-medium text-ds-secondary">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="text-[14px] leading-[18px] font-semibold text-ds-primary tabular-nums">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex min-w-0 flex-col gap-0.5">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function ArticleRow(t0) {
  const $ = _c(113);
  const {
    article,
    arbitrage,
    onEdit
  } = t0;
  const {
    t
  } = useTranslation();
  const fmt = useFormatPrice();
  const update = useArticleUpdate();
  const m = article.metrics;
  const switchable = article.status === "active" || article.status === "disabled";
  const t1 = `partner.channel.${article.channel}`;
  let t2;
  if ($[0] !== t || $[1] !== t1) {
    t2 = t(t1);
    $[0] = t;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== article.campaign || $[4] !== article.placement || $[5] !== t2) {
    t3 = [t2, article.placement, article.campaign].filter(Boolean);
    $[3] = article.campaign;
    $[4] = article.placement;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const source = t3.join(" \xB7 ");
  let t4;
  if ($[7] !== article.productImage) {
    t4 = article.productImage && <img src={article.productImage} alt="" loading="lazy" className="size-full object-cover" />;
    $[7] = article.productImage;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <div className="size-14 shrink-0 overflow-hidden rounded-[12px] bg-white">{t4}</div>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== article.code) {
    t6 = <CodeChip code={article.code} />;
    $[11] = article.code;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== article.status) {
    t7 = <StatusBadge status={article.status} />;
    $[13] = article.status;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <div className="flex min-w-0 flex-wrap items-center gap-1.5">{t6}{t7}</div>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== article.name) {
    t9 = <p className="truncate text-[14px] leading-[18px] font-semibold text-ds-ink" title={article.name}>{article.name}</p>;
    $[18] = article.name;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = `${article.productTitle} · ${source}`;
  let t11;
  if ($[20] !== article.productTitle || $[21] !== source || $[22] !== t10) {
    t11 = <p className="truncate text-[12px] leading-4 text-ds-secondary" title={t10}>{article.productTitle} · {source}</p>;
    $[20] = article.productTitle;
    $[21] = source;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== article.statusReason) {
    t12 = article.statusReason && <p className="text-[12px] leading-4 text-ds-error">{article.statusReason}</p>;
    $[24] = article.statusReason;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11 || $[27] !== t12 || $[28] !== t8 || $[29] !== t9) {
    t13 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t8}{t9}{t11}{t12}</div>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t8;
    $[29] = t9;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13 || $[32] !== t5) {
    t14 = <div className="flex min-w-0 gap-3">{t5}{t13}</div>;
    $[31] = t13;
    $[32] = t5;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const t15 = arbitrage ? "sm:grid-cols-6" : "sm:grid-cols-5";
  let t16;
  if ($[34] !== t15) {
    t16 = cn("grid grid-cols-3 gap-2 rounded-[14px] bg-white p-2.5 md:bg-transparent md:p-0", t15);
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t) {
    t17 = t("partner.articles.clicks");
    $[36] = t;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== m.clicks) {
    t18 = fmtInt(m.clicks);
    $[38] = m.clicks;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t17 || $[41] !== t18) {
    t19 = <Metric label={t17} value={t18} />;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t) {
    t20 = t("partner.articles.orders");
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== m.orders) {
    t21 = fmtInt(m.orders);
    $[45] = m.orders;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t20 || $[48] !== t21) {
    t22 = <Metric label={t20} value={t21} />;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t) {
    t23 = t("partner.articles.completed");
    $[50] = t;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== m.completedOrders) {
    t24 = fmtInt(m.completedOrders);
    $[52] = m.completedOrders;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t23 || $[55] !== t24) {
    t25 = <Metric label={t23} value={t24} />;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== arbitrage || $[58] !== fmt || $[59] !== m.cr || $[60] !== m.gmv || $[61] !== m.returns || $[62] !== t) {
    t26 = arbitrage ? <><Metric label="CR" value={fmtPercent(m.cr)} /><Metric label={t("partner.articles.returns")} value={fmtInt(m.returns)} /></> : <Metric label={t("partner.articles.gmv")} value={fmt(m.gmv)} />;
    $[57] = arbitrage;
    $[58] = fmt;
    $[59] = m.cr;
    $[60] = m.gmv;
    $[61] = m.returns;
    $[62] = t;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t) {
    t27 = t("partner.articles.earned");
    $[64] = t;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== fmt || $[67] !== m.commission) {
    t28 = fmt(m.commission);
    $[66] = fmt;
    $[67] = m.commission;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== t27 || $[70] !== t28) {
    t29 = <Metric label={t27} value={t28} />;
    $[69] = t27;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== t16 || $[73] !== t19 || $[74] !== t22 || $[75] !== t25 || $[76] !== t26 || $[77] !== t29) {
    t30 = <div className={t16}>{t19}{t22}{t25}{t26}{t29}</div>;
    $[72] = t16;
    $[73] = t19;
    $[74] = t22;
    $[75] = t25;
    $[76] = t26;
    $[77] = t29;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  const t31 = article.status === "active";
  const t32 = !switchable || update.isPending;
  let t33;
  if ($[79] !== article.id || $[80] !== t || $[81] !== update) {
    t33 = on => update.mutate({
      id: article.id,
      patch: {
        status: on ? "active" : "disabled"
      }
    }, {
      onSuccess: () => toast.success(on ? t("partner.articles.enabledToast") : t("partner.articles.disabledToast"))
    });
    $[79] = article.id;
    $[80] = t;
    $[81] = update;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== article.status || $[84] !== t) {
    t34 = article.status === "active" ? t("partner.articles.disable") : t("partner.articles.enable");
    $[83] = article.status;
    $[84] = t;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== t31 || $[87] !== t32 || $[88] !== t33 || $[89] !== t34) {
    t35 = <Switch checked={t31} disabled={t32} onCheckedChange={t33} aria-label={t34} data-testid="partner-article-toggle" />;
    $[86] = t31;
    $[87] = t32;
    $[88] = t33;
    $[89] = t34;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== article.status || $[92] !== t) {
    t36 = article.status === "active" ? t("partner.articles.on") : t("partner.articles.off");
    $[91] = article.status;
    $[92] = t;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  let t37;
  if ($[94] !== t36) {
    t37 = <span className="md:hidden">{t36}</span>;
    $[94] = t36;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  let t38;
  if ($[96] !== t35 || $[97] !== t37) {
    t38 = <div className="flex items-center gap-2 text-[13px] leading-[17px] font-medium text-ds-primary">{t35}{t37}</div>;
    $[96] = t35;
    $[97] = t37;
    $[98] = t38;
  } else {
    t38 = $[98];
  }
  const t39 = article.status === "blocked";
  let t40;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <Pencil />;
    $[99] = t40;
  } else {
    t40 = $[99];
  }
  let t41;
  if ($[100] !== t) {
    t41 = t("partner.articles.rename");
    $[100] = t;
    $[101] = t41;
  } else {
    t41 = $[101];
  }
  let t42;
  if ($[102] !== onEdit || $[103] !== t39 || $[104] !== t41) {
    t42 = <Button variant="ghost" size="sm" onClick={onEdit} disabled={t39}>{t40}{t41}</Button>;
    $[102] = onEdit;
    $[103] = t39;
    $[104] = t41;
    $[105] = t42;
  } else {
    t42 = $[105];
  }
  let t43;
  if ($[106] !== t38 || $[107] !== t42) {
    t43 = <div className="flex items-center justify-between gap-3 md:justify-end">{t38}{t42}</div>;
    $[106] = t38;
    $[107] = t42;
    $[108] = t43;
  } else {
    t43 = $[108];
  }
  let t44;
  if ($[109] !== t14 || $[110] !== t30 || $[111] !== t43) {
    t44 = <li className="grid gap-3 rounded-[20px] bg-ds-quaternary p-3 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1.7fr)_auto] md:items-center md:gap-4 md:p-4" data-testid="partner-article-row">{t14}{t30}{t43}</li>;
    $[109] = t14;
    $[110] = t30;
    $[111] = t43;
    $[112] = t44;
  } else {
    t44 = $[112];
  }
  return t44;
}
function RenameDialog(t0) {
  const $ = _c(25);
  const {
    article,
    onOpenChange
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = Boolean(article);
  let t2;
  if ($[0] !== t) {
    t2 = t("partner.articles.rename");
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <DialogTitle className="min-w-0 flex-1 text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-ds-ink">{t2}</DialogTitle>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("common.close");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <XIcon />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <DialogClose aria-label={t4} className="grid size-6 shrink-0 place-items-center rounded-[4px] text-ds-primary outline-none hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus">{t5}</DialogClose>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t6) {
    t7 = <div className="flex items-start gap-2.5 px-4 pt-4 pb-2.5 md:px-5">{t3}{t6}</div>;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = article?.code;
  let t9;
  if ($[12] !== t8) {
    t9 = <DialogDescription className="sr-only">{t8}</DialogDescription>;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== article || $[15] !== onOpenChange) {
    t10 = article && <RenameForm key={article.id} article={article} onDone={() => onOpenChange(false)} />;
    $[14] = article;
    $[15] = onOpenChange;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t7 || $[19] !== t9) {
    t11 = <DialogContent showCloseButton={false} overlayClassName="bg-black/20 backdrop-blur-[3px]" className="max-h-[92dvh] w-[calc(100vw-32px)] gap-0 overflow-y-auto rounded-[20px] border-0 bg-white p-0 shadow-none sm:max-w-[440px]">{t7}{t9}{t10}</DialogContent>;
    $[17] = t10;
    $[18] = t7;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== onOpenChange || $[22] !== t1 || $[23] !== t11) {
    t12 = <Dialog open={t1} onOpenChange={onOpenChange}>{t11}</Dialog>;
    $[21] = onOpenChange;
    $[22] = t1;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function RenameForm(t0) {
  const $ = _c(52);
  const {
    article,
    onDone
  } = t0;
  const {
    t
  } = useTranslation();
  const update = useArticleUpdate();
  const [name, setName] = useState(article.name);
  const [placement, setPlacement] = useState(article.placement);
  const [campaign, setCampaign] = useState(article.campaign);
  let t1;
  if ($[0] !== article.id || $[1] !== campaign || $[2] !== name || $[3] !== onDone || $[4] !== placement || $[5] !== t || $[6] !== update) {
    t1 = e => {
      e.preventDefault();
      if (!name.trim()) {
        return;
      }
      update.mutate({
        id: article.id,
        patch: {
          name: name.trim(),
          placement: placement.trim(),
          campaign: campaign.trim()
        }
      }, {
        onSuccess: () => {
          toast.success(t("partner.articles.saved"));
          onDone();
        }
      });
    };
    $[0] = article.id;
    $[1] = campaign;
    $[2] = name;
    $[3] = onDone;
    $[4] = placement;
    $[5] = t;
    $[6] = update;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t) {
    t2 = t("partner.generate.name");
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e_0 => setName(e_0.target.value);
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== name || $[12] !== t) {
    t4 = !name.trim() ? t("partner.generate.nameRequired") : undefined;
    $[11] = name;
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== name || $[15] !== t2 || $[16] !== t4) {
    t5 = <TextField label={t2} value={name} maxLength={120} onChange={t3} error={t4} />;
    $[14] = name;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t) {
    t6 = t("partner.generate.placement");
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e_1 => setPlacement(e_1.target.value);
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== placement || $[22] !== t6) {
    t8 = <TextField label={t6} value={placement} maxLength={160} onChange={t7} />;
    $[21] = placement;
    $[22] = t6;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t) {
    t9 = t("partner.generate.campaign");
    $[24] = t;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_2 => setCampaign(e_2.target.value);
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== campaign || $[28] !== t9) {
    t11 = <TextField label={t9} value={campaign} maxLength={160} onChange={t10} />;
    $[27] = campaign;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== name || $[31] !== update.isPending) {
    t12 = update.isPending || !name.trim();
    $[30] = name;
    $[31] = update.isPending;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t) {
    t13 = t("partner.articles.save");
    $[33] = t;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t12 || $[36] !== t13) {
    t14 = <Button type="submit" size="md" disabled={t12}>{t13}</Button>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t) {
    t15 = t("partner.articles.cancel");
    $[38] = t;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== onDone || $[41] !== t15) {
    t16 = <Button type="button" variant="gray" size="md" onClick={onDone}>{t15}</Button>;
    $[40] = onDone;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t14 || $[44] !== t16) {
    t17 = <div className="mt-1 flex gap-2">{t14}{t16}</div>;
    $[43] = t14;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t1 || $[47] !== t11 || $[48] !== t17 || $[49] !== t5 || $[50] !== t8) {
    t18 = <form className="flex flex-col gap-3 px-4 pb-5 md:px-5" onSubmit={t1}>{t5}{t8}{t11}{t17}</form>;
    $[46] = t1;
    $[47] = t11;
    $[48] = t17;
    $[49] = t5;
    $[50] = t8;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  return t18;
}